import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CheckCircle2, ShieldCheck, Tag } from "lucide-react";
import { toast } from "sonner";
import Reveal from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { useCart } from "@/context/CartContext";
import { api, formatApiErrorDetail, imgUrl, usd } from "@/lib/api";
import { useLang } from "@/i18n";

const EMPTY = { name: "", email: "", phone: "", company: "", address: "", notes: "" };

export default function Checkout() {
  const { items, total, clear } = useCart();
  const [form, setForm] = useState(EMPTY);
  const [method, setMethod] = useState("card");
  const [placing, setPlacing] = useState(false);
  const [placed, setPlaced] = useState(null);
  const [promoInput, setPromoInput] = useState("");
  const [promo, setPromo] = useState(null);
  const [promoError, setPromoError] = useState("");
  const [promoLoading, setPromoLoading] = useState(false);
  const navigate = useNavigate();
  const { t } = useLang();

  const discount = promo?.discount || 0;
  const grandTotal = Math.max(0, total - discount);

  // Auto-apply a kit-builder promo code carried over from /kit-builder
  useEffect(() => {
    const kitCode = localStorage.getItem("kinos_kit_promo");
    if (!kitCode) return;
    localStorage.removeItem("kinos_kit_promo");
    api.post("/promos/validate", { code: kitCode, subtotal: total })
      .then(({ data }) => {
        setPromo(data);
        setPromoInput(data.code);
        toast.success(`${t("promo_applied")}: ${data.code} (-${usd(data.discount)})`);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyPromo = async () => {
    if (!promoInput.trim()) return;
    setPromoLoading(true);
    setPromoError("");
    try {
      const { data } = await api.post("/promos/validate", { code: promoInput.trim(), subtotal: total });
      setPromo(data);
      toast.success(`${t("promo_applied")}: ${data.code} (-${usd(data.discount)})`);
    } catch (err) {
      setPromo(null);
      setPromoError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setPromoLoading(false);
    }
  };

  const placeOrder = async (e) => {
    e.preventDefault();
    setPlacing(true);
    try {
      if (method === "card") {
        const { data } = await api.post("/payments/cart-checkout", {
          customer: form,
          items: items.map((i) => ({ product_id: i.id, qty: i.qty })),
          origin_url: window.location.origin,
          promo_code: promo?.code || null,
        });
        window.location.href = data.checkout_url;
        return;
      }
      const { data } = await api.post("/orders", {
        customer: form,
        items: items.map((i) => ({ product_id: i.id, qty: i.qty })),
        method,
        promo_code: promo?.code || null,
      });
      setPlaced(data);
      clear();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setPlacing(false);
    }
  };

  if (placed) {
    return (
      <main className="min-h-screen bg-[#0B1120] flex items-center justify-center px-6 pt-24 pb-16">
        <Reveal>
          <div
            data-testid="order-success-panel"
            className="max-w-lg w-full bg-[#0F172A] border border-white/10 p-10 text-center"
          >
            <CheckCircle2 size={56} className="text-[#FFC61A] mx-auto mb-6" />
            <h1 className="font-display font-semibold text-white text-3xl mb-3">
              {t("order_received").replace(".", "")}<span className="text-[#ED1C24]">.</span>
            </h1>
            <p className="text-slate-400 text-sm mb-2">{t("your_order_number")}</p>
            <p data-testid="order-number" className="font-display text-2xl text-[#FFC61A] mb-6">
              {placed.order_number}
            </p>
            <p className="text-slate-400 text-sm leading-relaxed mb-2">
              {t("total")}: <span className="text-white">{usd(placed.total)}</span> —{" "}
              {placed.method === "quote" ? t("order_note_quote") : t("order_note_demo")}
            </p>
            {placed.method === "demo" && (
              <p className="text-[11px] text-slate-600 uppercase tracking-[0.15em] mb-6">
                {t("demo_disclaimer")}
              </p>
            )}
            <div className="flex gap-3 mt-8">
              <button
                data-testid="order-success-shop-btn"
                onClick={() => navigate("/shop")}
                className="flex-1 bg-[#ED1C24] hover:bg-[#C81016] text-white font-medium py-3.5 transition-colors duration-300"
              >
                {t("back_shop")}
              </button>
              <button
                data-testid="order-success-home-btn"
                onClick={() => navigate("/")}
                className="flex-1 border border-white/15 hover:border-[#ED1C24] text-slate-300 py-3.5 text-sm transition-colors duration-300"
              >
                Home
              </button>
            </div>
          </div>
        </Reveal>
      </main>
    );
  }

  if (items.length === 0) {
    return (
      <main className="min-h-screen bg-[#0B1120] flex items-center justify-center px-6 pt-24">
        <div data-testid="checkout-empty-state" className="text-center">
          <p className="font-display text-3xl text-white mb-4">{t("cart_empty_title")}</p>
          <Link
            data-testid="checkout-empty-shop-link"
            to="/shop"
            className="inline-block bg-[#ED1C24] hover:bg-[#C81016] text-white font-medium px-8 py-3.5 transition-colors duration-300"
          >
            {t("browse_shop")}
          </Link>
        </div>
      </main>
    );
  }

  const field =
    "bg-[#0B1120] border-white/10 text-slate-100 placeholder:text-slate-600 focus-visible:ring-[#ED1C24] rounded-none h-12";

  return (
    <main data-testid="checkout-page" className="min-h-screen bg-[#0B1120] px-6 md:px-12 lg:px-24 pt-32 pb-24">
      <Reveal y={24}>
        <p className="text-xs tracking-[0.25em] uppercase text-[#ED1C24] mb-4">{t("checkout_label")}</p>
        <h1 className="font-display font-semibold text-white text-4xl sm:text-5xl tracking-tight mb-12">
          {t("checkout_title").replace(".", "")}<span className="text-[#FFC61A]">.</span>
        </h1>
      </Reveal>

      <div className="grid lg:grid-cols-5 gap-10">
        <form
          data-testid="checkout-form"
          onSubmit={placeOrder}
          className="lg:col-span-3 bg-[#0F172A] border border-white/10 p-8 space-y-5"
        >
          <h2 className="font-display text-white text-xl mb-2">{t("checkout_contact")}</h2>
          <div className="grid sm:grid-cols-2 gap-5">
            <Input data-testid="checkout-name-input" required placeholder={t("ph_name")} value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })} className={field} />
            <Input data-testid="checkout-email-input" required type="email" placeholder={t("ph_email")} value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })} className={field} />
            <Input data-testid="checkout-phone-input" placeholder={t("ph_phone")} value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })} className={field} />
            <Input data-testid="checkout-company-input" placeholder={t("ph_company")} value={form.company}
              onChange={(e) => setForm({ ...form, company: e.target.value })} className={field} />
          </div>
          <Input data-testid="checkout-address-input" placeholder={t("ph_address")} value={form.address}
            onChange={(e) => setForm({ ...form, address: e.target.value })} className={field} />
          <Textarea data-testid="checkout-notes-input" placeholder={t("ph_notes")} rows={3} value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            className="bg-[#0B1120] border-white/10 text-slate-100 placeholder:text-slate-600 focus-visible:ring-[#ED1C24] rounded-none" />

          <h2 className="font-display text-white text-xl pt-4">{t("pay_method")}</h2>
          <RadioGroup value={method} onValueChange={setMethod} className="space-y-3" data-testid="payment-method-group">
            <Label
              htmlFor="method-card"
              className={`flex items-start gap-4 border p-5 cursor-pointer transition-colors duration-300 ${
                method === "card" ? "border-[#ED1C24] bg-[#ED1C24]/5" : "border-white/10 hover:border-white/30"
              }`}
            >
              <RadioGroupItem value="card" id="method-card" data-testid="method-card-radio" className="mt-1" />
              <span>
                <span className="block text-white text-sm font-medium">{t("card_title")}</span>
                <span className="block text-xs text-slate-500 mt-1">{t("card_desc")}</span>
              </span>
            </Label>
            <Label
              htmlFor="method-demo"
              className={`flex items-start gap-4 border p-5 cursor-pointer transition-colors duration-300 ${
                method === "demo" ? "border-[#ED1C24] bg-[#ED1C24]/5" : "border-white/10 hover:border-white/30"
              }`}
            >
              <RadioGroupItem value="demo" id="method-demo" data-testid="method-demo-radio" className="mt-1" />
              <span>
                <span className="block text-white text-sm font-medium">{t("demo_title")}</span>
                <span className="block text-xs text-slate-500 mt-1">{t("demo_desc")}</span>
              </span>
            </Label>
            <Label
              htmlFor="method-quote"
              className={`flex items-start gap-4 border p-5 cursor-pointer transition-colors duration-300 ${
                method === "quote" ? "border-[#FFC61A] bg-[#FFC61A]/5" : "border-white/10 hover:border-white/30"
              }`}
            >
              <RadioGroupItem value="quote" id="method-quote" data-testid="method-quote-radio" className="mt-1" />
              <span>
                <span className="block text-white text-sm font-medium">{t("quote_title")}</span>
                <span className="block text-xs text-slate-500 mt-1">{t("quote_desc")}</span>
              </span>
            </Label>
          </RadioGroup>

          <button
            data-testid="place-order-btn"
            type="submit"
            disabled={placing}
            className="w-full bg-[#ED1C24] hover:bg-[#C81016] disabled:opacity-60 text-white font-medium py-4 transition-all duration-300 hover:shadow-[0_0_30px_rgba(237,28,36,0.4)]"
          >
            {placing
              ? t("processing")
              : method === "card"
                ? `${t("pay_by_card")} — ${usd(grandTotal)}`
                : method === "quote"
                  ? t("request_quote")
                  : t("place_order")}
          </button>
        </form>

        <div className="lg:col-span-2">
          <div className="bg-[#0F172A] border border-white/10 p-8 sticky top-28">
            <h2 className="font-display text-white text-xl mb-6">{t("summary")}</h2>
            <div className="space-y-4 max-h-72 overflow-y-auto pr-1">
              {items.map((i) => (
                <div key={i.id} data-testid={`checkout-item-${i.id}`} className="flex gap-3 items-center">
                  <img src={imgUrl(i.image)} alt={i.name} className="w-14 h-14 object-cover" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white truncate">{i.name}</p>
                    <p className="text-xs text-slate-500">Qty {i.qty}</p>
                  </div>
                  <span className="text-sm text-[#FFC61A]">{usd(i.price * i.qty)}</span>
                </div>
              ))}
            </div>

            <div className="mt-6 pt-5 border-t border-white/10">
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Tag size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                  <Input
                    data-testid="promo-code-input"
                    placeholder={t("promo_ph")}
                    value={promoInput}
                    onChange={(e) => setPromoInput(e.target.value)}
                    disabled={!!promo}
                    className="bg-[#0B1120] border-white/10 text-slate-100 placeholder:text-slate-600 focus-visible:ring-[#ED1C24] rounded-none h-11 pl-9 uppercase"
                  />
                </div>
                {promo ? (
                  <button
                    type="button"
                    data-testid="promo-remove-btn"
                    onClick={() => { setPromo(null); setPromoInput(""); }}
                    className="px-4 border border-white/15 text-slate-300 hover:border-[#ED1C24] text-xs transition-colors duration-300"
                  >
                    ✕
                  </button>
                ) : (
                  <button
                    type="button"
                    data-testid="promo-apply-btn"
                    onClick={applyPromo}
                    disabled={promoLoading || !promoInput.trim()}
                    className="px-5 bg-white/10 hover:bg-[#ED1C24] disabled:opacity-50 text-white text-xs font-medium transition-colors duration-300"
                  >
                    {promoLoading ? "..." : t("promo_apply")}
                  </button>
                )}
              </div>
              {promoError && <p data-testid="promo-error" className="text-[#ED1C24] text-xs mt-2">{promoError}</p>}
              {promo && (
                <p data-testid="promo-success" className="text-emerald-400 text-xs mt-2">
                  {promo.code}: {promo.discount_type === "percent" ? `${promo.value}%` : usd(promo.value)} off
                </p>
              )}
            </div>

            <div className="border-t border-white/10 mt-5 pt-5 space-y-2">
              <div className="flex justify-between text-sm text-slate-400">
                <span>{t("cart_subtotal")}</span>
                <span>{usd(total)}</span>
              </div>
              {discount > 0 && (
                <div className="flex justify-between text-sm text-emerald-400" data-testid="checkout-discount-row">
                  <span>{t("discount")} ({promo.code})</span>
                  <span>-{usd(discount)}</span>
                </div>
              )}
              <div className="flex justify-between text-sm text-slate-400">
                <span>{t("delivery")}</span>
                <span>{t("delivery_val")}</span>
              </div>
              <div className="flex justify-between items-baseline">
                <span className="text-sm text-slate-400">{t("total")}</span>
                <span data-testid="checkout-total" className="font-display text-2xl text-white">{usd(grandTotal)}</span>
              </div>
            </div>
            <p className="flex items-center gap-2 text-[11px] text-slate-500 mt-6">
              <ShieldCheck size={14} className="text-[#FFC61A]" />
              {t("warranty_line")}
            </p>
          </div>
        </div>
      </div>
    </main>
  );
}
